import { DatePicker, Input, PolicyDispatchingPermission, Select, assets } from '@pulseops/common'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { ActivityIndicator, Text, View } from 'react-native'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import PDSignatureDialog from './components/PDSignatureDialog'
import { usePolicyDispatchingOffice } from './components/PolicyDispatchingOfficeContext'
import {
  AckManagementSavePayload,
  AckManagementSearchItem,
  PolicyDispatchingService
} from './services/PolicyDispatchingService'
import {
  borderedDatePickerContainerStyle,
  borderedInputStyle,
  borderedSelectStyle
} from './styles/policyDispatchingStyles'
import { formatDate, getACKStatus, getPermissions, parseDateString } from './utils/policyDispatchingUtils'
import { pipe } from 'fp-ts/function'
import { ZIO } from '@mxt/zio'

type ACKFormValues = {
  contractNumber: string
  customerSignedDate: Date | null
  letterType: { label: string; value: string } | null
  contactStatus: { label: string; value: string } | null
  remark: string
  postOfficeReturnDate: Date | null
  recipientName: string
  correctContactAddress: string
}

const letterTypeOptions = [
  { value: 'Bản gốc', label: 'Bản gốc' },
  { value: 'Bưu điện xác nhận', label: 'Bưu điện xác nhận' }
]

const contactStatusOptions = [
  { value: 'LOST_CONTACT', label: 'Mất liên lạc' },
  { value: 'COMPLETED', label: 'Hoàn tất' },
  { value: 'NOT_CONTACTED', label: 'Chưa liên lạc' }
]

export default function PDACKScreen() {
  const { officeCode } = usePolicyDispatchingOffice()
  const [searchResult, setSearchResult] = useState<AckManagementSearchItem | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const [openSignatureModal, setOpenSignatureModal] = useState<boolean>(false)
  const [modalTitle, setModalTitle] = useState<string>('Thông báo')
  const [modalMessage, setModalMessage] = useState<string>('')
  const permissions = getPermissions()
  const allowSave = permissions.includes(PolicyDispatchingPermission.AckLetterSave)
  const allowDelete = permissions.includes(PolicyDispatchingPermission.AckLetterDelete)
  const allowViewSignature = permissions.includes(PolicyDispatchingPermission.AckLetterViewSignature)

  const { control, handleSubmit, setValue, watch } = useForm<ACKFormValues>({
    defaultValues: {
      contractNumber: '',
      customerSignedDate: null,
      letterType: null,
      remark: '',
      contactStatus: contactStatusOptions[0],
      postOfficeReturnDate: null,
      recipientName: '',
      correctContactAddress: ''
    }
  })

  const contactStatusValue = watch('contactStatus')?.value?.normalize() ?? ''

  const handleSearch = () => {
    if (!watch('contractNumber') || !watch('contractNumber').trim()) {
      setModalTitle('Thông báo')
      setModalMessage('Vui lòng nhập số hợp đồng.')
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchAckManagement(watch('contractNumber').trim()),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          if (response && 'body' in response && response.body) {
            const data = response.body
            setSearchResult(data)
            setValue('customerSignedDate', data.customerSignedDate ? parseDateString(data.customerSignedDate) : null)
            setValue(
              'letterType',
              data.letterType
                ? letterTypeOptions.find(
                    (opt) => opt.value.trim().normalize() === data.letterType?.trim().normalize()
                  ) || {
                    label: data.letterType,
                    value: data.letterType
                  }
                : null
            )
            setValue('remark', data.remark || '')
            setValue(
              'contactStatus',
              data.contactStatus
                ? contactStatusOptions.find(
                    (opt) => opt.value.trim().normalize() === data.contactStatus?.trim().normalize()
                  ) || { label: data.contactStatus, value: data.contactStatus }
                : null
            )
            setValue(
              'postOfficeReturnDate',
              data.postOfficeReturnDate ? parseDateString(data.postOfficeReturnDate) : null
            )
            setValue('recipientName', data.recipientName || '')
            setValue('correctContactAddress', data.correctContactAddress || '')
          } else {
            setSearchResult(null)
            setModalTitle('Thông báo')
            setModalMessage('Không tìm thấy thông tin hợp đồng.')
            setValue('customerSignedDate', null)
            setValue('letterType', null)
            setValue('remark', '')
            setValue('contactStatus', null)
            setValue('postOfficeReturnDate', null)
            setValue('recipientName', '')
            setValue('correctContactAddress', '')
            setOpenModal(true)
          }
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setSearchResult(null)
          setModalTitle('Lỗi')
          setModalMessage('Có lỗi xảy ra khi tìm kiếm.')
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleSave = (values: ACKFormValues) => {
    if (!officeCode) {
      setModalTitle('Văn phòng')
      setModalMessage('Vui lòng chọn văn phòng trước khi lưu.')
      setOpenModal(true)
      return
    }

    if (!searchResult?.contractNumber && !watch('contractNumber')?.trim()) {
      setModalTitle('Thông báo')
      setModalMessage('Vui lòng nhập số hợp đồng.')
      setOpenModal(true)
      return
    }

    const payload: AckManagementSavePayload = {
      contractNumber: searchResult?.contractNumber || watch('contractNumber').trim(),
      officeCode,
      deliveryDate: searchResult?.deliveryDate || '',
      customerSignedDate: formatDate(values.customerSignedDate) || '',
      letterType: values.letterType?.value || '',
      remark: values.remark || '',
      contactStatus: values.contactStatus?.value || '',
      postOfficeReturnDate: formatDate(values.postOfficeReturnDate) || '',
      recipientName: values.recipientName || '',
      correctContactAddress: values.correctContactAddress || ''
    }

    const isUpdate = Boolean(searchResult?.existed)
    const apiCall = isUpdate
      ? PolicyDispatchingService.updateAckManagement(payload)
      : PolicyDispatchingService.createAckManagement(payload)

    setIsLoading(true)
    pipe(
      apiCall,
      ZIO.tap((response: any) =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          if (
            response &&
            response.responseStatus &&
            response.responseStatus.code !== 1 &&
            response.responseStatus.code !== 200
          ) {
            setModalTitle('Thông báo')
            setModalMessage(response.responseStatus.message || 'Lưu thông tin không thành công.')
            setOpenModal(true)
          } else {
            setModalTitle('Thông báo')
            setModalMessage('Lưu thông tin thư xác nhận thành công.')
            setOpenModal(true)
            handleSearch()
          }
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalTitle('Lỗi')
          setModalMessage('Có lỗi xảy ra khi lưu thông tin.')
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  const handleDelete = () => {
    const contractNum = searchResult?.contractNumber || watch('contractNumber')?.trim()
    if (!contractNum) {
      setModalTitle('Thông báo')
      setModalMessage('Vui lòng nhập số hợp đồng.')
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.deleteAckManagement(contractNum),
      ZIO.tap((response: any) =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          if (
            response &&
            response.responseStatus &&
            response.responseStatus.code !== 1 &&
            response.responseStatus.code !== 200
          ) {
            setModalTitle('Thông báo')
            setModalMessage(response.responseStatus.message || 'Xóa thông tin không thành công.')
            setOpenModal(true)
          } else {
            setModalTitle('Thông báo')
            setModalMessage('Xóa thông tin thư xác nhận thành công.')
            setOpenModal(true)
            setValue('customerSignedDate', null)
            setValue('letterType', null)
            setValue('remark', '')
          }
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalTitle('Lỗi')
          setModalMessage('Có lỗi xảy ra khi xóa thông tin.')
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Quản lý thư xác nhận</Text>

      <View style={styles.wrapper}>
        <View style={{ ...styles.row, alignItems: 'flex-end' } as any}>
          <View style={styles.column_1_3}>
            <Controller
              name="contractNumber"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Input
                  title={'Số hợp đồng'}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  errorMessage={error?.message}
                  containerStyle={{ flex: 1 }}
                  inputStyle={borderedInputStyle}
                  filled
                  required
                  maxLength={8}
                />
              )}
            />
          </View>
          <button style={styles.submitButton} onClick={handleSearch} disabled={isLoading}>
            <span style={styles.mr_5}>
              <assets.SearchWhite />
            </span>
            Tìm
          </button>
        </View>
      </View>

      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}

      <View style={{ marginTop: 24, ...styles.wrapper } as any}>
        <View style={{ display: 'flex', flexDirection: 'column', gap: 16 } as any}>
          <View style={styles.row}>
            <View style={styles.column_1_3}>
              <Text style={styles.label}>Ngày phát hành</Text>
              <Text style={styles.value}>{searchResult?.policyIssueDate || '-'}</Text>
            </View>
            <View style={styles.column_1_3}>
              <Text style={styles.label}>Ngày giao</Text>
              <Text style={styles.value}>{searchResult?.deliveryDate || '-'}</Text>
            </View>
            <View style={styles.column_1_3}>
              <Text style={styles.label}>Tình trạng hợp đồng</Text>
              <Text style={styles.value}>{getACKStatus(searchResult?.status) || '-'}</Text>
            </View>
          </View>

          <View style={styles.row}>
            <View style={styles.column_1_3}>
              <Text style={styles.label}>Mã đại lý</Text>
              <Text style={styles.value}>{searchResult?.agentNumber || '-'}</Text>
            </View>
            <View style={styles.column_1_3}>
              <Text style={styles.label}>Tên Đại lý</Text>
              <Text style={styles.value}>{searchResult?.agentName || '-'}</Text>
            </View>
            <View style={styles.column_1_3} />
          </View>

          <View style={styles.row}>
            <View style={styles.column_1_3}>
              <Text style={styles.label}>Mã đại lý hỗ trợ</Text>
              <Text style={styles.value}>{searchResult?.shareAgentNumber || '-'}</Text>
            </View>
            <View style={styles.column_1_3}>
              <Text style={styles.label}>Tên đại lý hỗ trợ</Text>
              <Text style={styles.value}>{searchResult?.shareAgentName || '-'}</Text>
            </View>
            <View style={styles.column_1_3}>
              <button
                style={searchResult && allowViewSignature ? styles.button : styles.disabledButton}
                onClick={() => setOpenSignatureModal(true)}
                disabled={!searchResult || !allowViewSignature}
              >
                <span style={{ ...styles.mr_5, marginTop: 3 }}>
                  <assets.PDEye />
                </span>
                Xem chữ ký
              </button>
            </View>
          </View>
        </View>
      </View>

      <View style={{ marginTop: 24, ...styles.wrapper } as any}>
        <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 } as any}>
          <Text style={styles.subTitle}> - Thông tin thư xác nhận</Text>
          <View
            style={
              {
                borderBottomWidth: 1,
                borderBottomStyle: 'solid',
                borderBottomColor: '#ccc',
                flex: 1,
                marginTop: 4
              } as any
            }
          />
        </View>

        <View style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 24 } as any}>
          <View style={styles.row}>
            <View style={styles.column_1_3}>
              <Controller
                name="customerSignedDate"
                control={control}
                rules={{
                  required: {
                    value: watch('letterType')?.value?.normalize() === 'Bản gốc'.normalize() ? true : false,
                    message: 'Ngày KH ký thư là bắt buộc'
                  },
                  validate: (val) => {
                    if (!val || !searchResult?.deliveryDate) return true
                    const deliveryDate = parseDateString(searchResult.deliveryDate)
                    if (deliveryDate) {
                      const signed = new Date(val)
                      signed.setHours(0, 0, 0, 0)
                      const delivery = new Date(deliveryDate)
                      delivery.setHours(0, 0, 0, 0)
                      if (signed < delivery) {
                        return 'Ngày KH ký thư phải lớn hơn hoặc bằng ngày giao'
                      }
                    }
                    return true
                  }
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <DatePicker
                    required={watch('letterType')?.value?.normalize() === 'Bản gốc'.normalize() ? true : false}
                    label={'Ngày KH ký thư'}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    maxDate={undefined}
                    minDate={undefined}
                    minDateMessage={''}
                    errorMessage={error?.message}
                    inputStyle={{ paddingBottom: 3 }}
                    hideUnderline
                    containerStyle={borderedDatePickerContainerStyle}
                    alwaysShow={watch('letterType')?.value?.normalize() === 'Bản gốc'.normalize() ? false : true}
                  />
                )}
              />
            </View>
            <View style={styles.column_1_3} />
            <View style={styles.column_1_3} />
          </View>

          <View style={styles.row}>
            <View style={styles.column_1_3}>
              <Controller
                name="letterType"
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: 'Loại thư là bắt buộc'
                  }
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <Select
                    label={'Loại thư'}
                    options={letterTypeOptions}
                    placeholder={'Chọn loại thư'}
                    onChange={(e) => {
                      onChange(e)
                    }}
                    onBlur={onBlur}
                    value={value}
                    key={value?.value}
                    errorMessage={error?.message}
                    containerStyles={borderedSelectStyle}
                    filled
                    required
                  />
                )}
              />
            </View>
            <View style={styles.column_1_3} />
            <View style={styles.column_1_3} />
          </View>

          {watch('letterType')?.value.normalize() === 'Bưu điện xác nhận'.normalize() && (
            <>
              <View style={styles.row}>
                <View style={styles.column_1_3}>
                  <Controller
                    name="contactStatus"
                    control={control}
                    rules={{
                      required: {
                        value:
                          watch('letterType')?.value.normalize() === 'Bưu điện xác nhận'.normalize() ? true : false,
                        message: 'Tình trạng liên lạc là bắt buộc'
                      }
                    }}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <Select
                        label={'Tình trạng liên lạc'}
                        options={contactStatusOptions}
                        placeholder={'Chọn tình trạng liên lạc'}
                        onChange={(e) => {
                          onChange(e)
                        }}
                        onBlur={onBlur}
                        value={value}
                        key={value?.value}
                        errorMessage={error?.message}
                        containerStyles={borderedSelectStyle}
                        filled
                        required={
                          watch('letterType')?.value.normalize() === 'Bưu điện xác nhận'.normalize() ? true : false
                        }
                      />
                    )}
                  />
                </View>
                <View style={styles.column_1_3} />
                <View style={styles.column_1_3} />
              </View>

              <View style={styles.row}>
                <View style={styles.column_1_3}>
                  <Controller
                    name="postOfficeReturnDate"
                    control={control}
                    rules={{
                      required: {
                        value: ['LOST_CONTACT'.normalize(), 'NOT_CONTACTED'.normalize()].includes(contactStatusValue),
                        message: 'Ngày bưu điện trả về là bắt buộc'
                      }
                    }}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <DatePicker
                        required={['LOST_CONTACT'.normalize(), 'NOT_CONTACTED'.normalize()].includes(
                          contactStatusValue
                        )}
                        label={'Ngày bưu điện trả về'}
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        maxDate={undefined}
                        minDate={undefined}
                        minDateMessage={''}
                        errorMessage={error?.message}
                        inputStyle={{ paddingBottom: 3 }}
                        hideUnderline
                        containerStyle={borderedDatePickerContainerStyle}
                        alwaysShow={
                          !['LOST_CONTACT'.normalize(), 'NOT_CONTACTED'.normalize()].includes(contactStatusValue)
                        }
                      />
                    )}
                  />
                </View>
                <View style={styles.column_1_3} />
                <View style={styles.column_1_3} />
              </View>

              <View style={styles.row}>
                <View style={styles.column_1_3}>
                  <Controller
                    name="recipientName"
                    control={control}
                    render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                      <Input
                        title={'Người ký nhận'}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        errorMessage={error?.message}
                        containerStyle={{ flex: 1 }}
                        inputStyle={borderedInputStyle}
                        filled
                      />
                    )}
                  />
                </View>
                <View style={styles.column_1_3} />
                <View style={styles.column_1_3} />
              </View>

              {watch('contactStatus')?.value.normalize() === 'COMPLETED'.normalize() && (
                <View style={styles.row}>
                  <View style={styles.column_1_3}>
                    <Controller
                      name="correctContactAddress"
                      control={control}
                      rules={{
                        required: {
                          value: watch('contactStatus')?.value.normalize() === 'COMPLETED'.normalize(),
                          message: 'Địa chỉ liên hệ chính xác là bắt buộc'
                        }
                      }}
                      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                        <Input
                          title={'Địa chỉ liên hệ chính xác'}
                          onChange={onChange}
                          onBlur={onBlur}
                          value={value}
                          errorMessage={error?.message}
                          containerStyle={{ flex: 1 }}
                          multiline
                          numberOfLines={3}
                          required={watch('contactStatus')?.value.normalize() === 'COMPLETED'.normalize()}
                          inputStyle={{ ...borderedInputStyle, padding: 10 }}
                        />
                      )}
                    />
                  </View>
                  <View style={styles.column_1_3} />
                  <View style={styles.column_1_3} />
                </View>
              )}
            </>
          )}

          <View style={styles.row}>
            <View style={styles.column_1_3}>
              <Controller
                name="remark"
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <Input
                    title={'Ghi chú'}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    errorMessage={error?.message}
                    containerStyle={{ flex: 1 }}
                    multiline
                    numberOfLines={5}
                    inputStyle={{ ...borderedInputStyle, padding: 10 }}
                  />
                )}
              />
            </View>
            <View style={styles.column_1_3} />
            <View style={styles.column_1_3} />
          </View>
        </View>

        <View style={styles.bottomAction}>
          <button
            style={searchResult && allowSave ? styles.submitButton : styles.disabledButton}
            disabled={!searchResult || !allowSave || isLoading}
            onClick={handleSubmit(handleSave)}
          >
            <span style={styles.mr_5}>
              <assets.PDSave style={{ color: '#fff' }} />
            </span>
            Lưu
          </button>
          <button
            style={searchResult && allowDelete ? styles.submitButton : styles.disabledButton}
            disabled={!searchResult || !allowDelete || isLoading}
            onClick={handleDelete}
          >
            <span style={styles.mr_5}>
              <assets.PDTrash style={{ color: '#fff' }} />
            </span>
            Xóa
          </button>
        </View>
      </View>

      <PolicyDispatchingDialog
        visible={openModal}
        title={modalTitle}
        message={modalMessage}
        onClose={() => setOpenModal(false)}
      />

      <PDSignatureDialog
        open={openSignatureModal}
        onClose={() => setOpenSignatureModal(false)}
        info={{
          contractNumber: searchResult?.contractNumber,
          mainDoc: searchResult?.mainDoc,
          subDoc: searchResult?.subDoc,
          imageFile: searchResult?.imageFile
        }}
      />
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  wrapper: {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '16px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
    width: '100%'
  },
  column_1_3: {
    width: 'calc(100% / 3 - 16px)'
  },
  submitButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600'
  },
  button: {
    backgroundColor: '#fff',
    color: '#000',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5dc',
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600',
    width: 'fit-content'
  },
  mr_5: {
    marginRight: '5px'
  },
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: '#6a7282',
    marginBottom: 4
  },
  value: {
    fontSize: 14,
    fontWeight: 700,
    color: '#101828'
  },
  subTitle: {
    fontSize: '18px',
    fontWeight: '600',
    whiteSpace: 'nowrap' as const
  },
  bottomAction: {
    marginTop: 40,
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: '#ccc',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingTop: 20
  },
  loadingOverlay: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  },
  disabledButton: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600',
    cursor: 'not-allowed',
    opacity: 0.7,
    width: 'fit-content'
  }
}
